import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  type OnboardingApiClient,
} from '../onboarding/onboarding-api'
import type {
  ConfirmedOrganizationClinic,
  OrganizationResponse,
} from '../onboarding/organization-onboarding-contracts'
import {
  createReadSideRepository,
  type ReadSideRepository,
} from '../read-side/read-side-status'
import { OrganizationAdministrators } from './OrganizationAdministrators'
import { OrganizationAlertSettings } from './OrganizationAlertSettings'
import { OrganizationClinicDialog } from './OrganizationClinicDialog'
import {
  Status,
  useClinicStatus,
  type ManagerAccessView,
} from './organization-clinic-status'
import {
  managerLabel,
  onboardingStep,
  openInvitations,
  phoneNumberLabel,
  plural,
} from './organization-model'
import {
  administratorAccessQuery,
  managerAccessQuery,
} from './organization-queries'
import { canManageOrganization } from './settings-access'
import panel from './SettingsSection.module.css'
import styles from './OrganizationSettings.module.css'

export function OrganizationSettings({
  organization,
  apiClient,
  readSideRepository,
  onOpenClinicSettings,
}: {
  organization: OrganizationResponse
  apiClient?: OnboardingApiClient
  readSideRepository?: ReadSideRepository
  /** Opens one clinic's own Settings; Settings passes its section switch. */
  onOpenClinicSettings?: (tenantUid: string) => void
}) {
  const auth = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const notify = useScopedToast()
  const user = auth.status === 'authenticated' ? auth.user : null
  const api = apiClient ?? getOnboardingApiClient()
  const authorized = user !== null && canManageOrganization(organization)
  const organizationId = organization.organization.id
  const readSide = useMemo(
    () =>
      readSideRepository ?? createReadSideRepository({ getUser: () => user }),
    [readSideRepository, user]
  )
  const [openClinicId, setOpenClinicId] = useState<string | null>(null)

  const access = useQuery({
    ...managerAccessQuery(api, user, organizationId),
    enabled: authorized && !!api.getOrganizationAccess,
  })
  const administrators = useQuery({
    ...administratorAccessQuery(api, user, organizationId),
    enabled: authorized && !!api.getAdministratorAccess,
  })
  const checking =
    useIsFetching({ queryKey: ['organization-twilio'] }) +
      useIsFetching({ queryKey: ['organization-communications'] }) >
    0

  // A refetch that fails withdraws every manager control, even with data cached.
  const accessMismatch =
    access.data !== undefined && access.data.organizationId !== organizationId
  const accessVerified = authorized && access.isSuccess && !accessMismatch
  const managerAccess: ManagerAccessView = {
    verified: accessVerified,
    pending: access.isPending,
    failed: access.isError || accessMismatch,
    managers: accessVerified
      ? (access.data?.managers ?? []).filter(
          (manager) => manager.managerLifecycleStatus === 'ACTIVE'
        )
      : [],
    invitations: accessVerified
      ? openInvitations(access.data?.invitations ?? [])
      : [],
    retry: () => void access.refetch(),
    refresh: () =>
      queryClient.invalidateQueries({
        queryKey: ['organization-access-management', organizationId],
      }),
  }

  if (!authorized) {
    return (
      <StatePanel
        kind="forbidden"
        title="Organization settings unavailable"
        description="Only organization Owners and Administrators can manage clinics and access."
      />
    )
  }

  const openClinic = organization.clinics.find(
    (clinic) => clinic.tenantUid === openClinicId
  )
  const adminCount =
    administrators.isSuccess &&
    administrators.data.organizationId === organizationId
      ? administrators.data.administrators.length
      : null
  const summary = [
    plural(organization.clinics.length, 'clinic', 'clinics'),
    managerAccess.verified
      ? plural(managerAccess.managers.length, 'manager', 'managers')
      : null,
    adminCount !== null
      ? plural(adminCount, 'administrator', 'administrators')
      : null,
  ].filter(Boolean)

  function recheck() {
    void queryClient.invalidateQueries({ queryKey: ['organization-twilio'] })
    void queryClient.invalidateQueries({
      queryKey: ['organization-communications'],
    })
    for (const clinic of organization.clinics)
      void queryClient.invalidateQueries({
        queryKey: ['read-side', clinic.tenantUid],
      })
  }

  function openClinicSettings(tenantUid: string) {
    setOpenClinicId(null)
    if (onOpenClinicSettings) onOpenClinicSettings(tenantUid)
    else void navigate('/settings?section=clinic')
  }

  return (
    <section className={panel.section} aria-label="Organization">
      <header className={`${panel.panel} ${styles.summary}`}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>Organization</span>
          <h2>{organization.organization.name}</h2>
          <p className={styles.summaryMeta}>{summary.join(' · ')}</p>
        </div>
        <span className={styles.role}>
          <span className={panel.srOnly}>Your role: </span>
          {organization.role === 'OWNER' ? 'Owner' : 'Administrator'}
        </span>
      </header>

      <section
        className={`${panel.panel} ${styles.clinicsPanel}`}
        aria-labelledby="organization-clinics-title"
      >
        <div className={panel.panelHeader}>
          <div className={panel.heading}>
            <span className={panel.eyebrow}>Clinics</span>
            <h2 id="organization-clinics-title">Clinic workspaces</h2>
            <p>Phone &amp; texting and manager access for each clinic.</p>
          </div>
          <div className={panel.headerActions}>
            {organization.clinics.length > 0 ? (
              <Button
                type="button"
                variant="secondary"
                onClick={recheck}
                aria-busy={checking || undefined}
              >
                <RefreshCw
                  size={16}
                  aria-hidden="true"
                  className={checking ? panel.spin : undefined}
                />
                Recheck status
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={!organization.canAddClinic}
              onClick={() => void navigate('/organization/clinics/add')}
            >
              <Plus size={16} aria-hidden="true" /> Add clinic
            </Button>
          </div>
        </div>

        {managerAccess.failed ? (
          <div className={panel.notice} data-tone="warning" role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <div>
              <strong>Manager access couldn’t be loaded</strong>
              <p>
                Clinics are still listed. Manager changes are paused until
                access loads.
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={managerAccess.retry}
              >
                <RefreshCw size={14} aria-hidden="true" /> Retry
              </Button>
            </div>
          </div>
        ) : null}

        {organization.clinics.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <Building2 size={22} />
            </span>
            <h3>No clinics yet</h3>
            <p>Add a clinic by verifying its NexHealth Location ID.</p>
            <Button
              type="button"
              disabled={!organization.canAddClinic}
              onClick={() => void navigate('/organization/clinics/add')}
            >
              <Plus size={16} aria-hidden="true" /> Add clinic
            </Button>
          </div>
        ) : (
          <div className={styles.tableFrame}>
            <table className={styles.table} aria-label="Organization clinics">
              <thead>
                <tr>
                  <th scope="col">Clinic</th>
                  <th scope="col">Workspace</th>
                  <th scope="col">Phone &amp; texting</th>
                  <th scope="col">Managers</th>
                  <th scope="col">
                    <span className={panel.srOnly}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {organization.clinics.map((clinic) => (
                  <ClinicRow
                    key={clinic.tenantUid}
                    clinic={clinic}
                    api={api}
                    readSide={readSide}
                    managerAccess={managerAccess}
                    onManage={() => setOpenClinicId(clinic.tenantUid)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <OrganizationAdministrators
        organization={organization}
        apiClient={api}
        notify={notify}
      />

      <OrganizationAlertSettings
        organization={organization}
        apiClient={api}
        notify={notify}
      />

      {openClinic ? (
        <OrganizationClinicDialog
          clinic={openClinic}
          api={api}
          readSide={readSide}
          managerAccess={managerAccess}
          notify={notify}
          onOpenClinicSettings={openClinicSettings}
          onClose={() => setOpenClinicId(null)}
        />
      ) : null}
    </section>
  )
}

function ClinicRow({
  clinic,
  api,
  readSide,
  managerAccess,
  onManage,
}: {
  clinic: ConfirmedOrganizationClinic
  api: OnboardingApiClient
  readSide: ReadSideRepository
  managerAccess: ManagerAccessView
  onManage: () => void
}) {
  const { phone, workspace } = useClinicStatus(clinic, api, readSide, true)
  const step = onboardingStep(clinic)
  const managers = managerAccess.managers.filter((manager) =>
    manager.tenantUids.includes(clinic.tenantUid)
  )
  const invitations = managerAccess.invitations.filter(
    (invitation) => invitation.tenantUid === clinic.tenantUid
  )
  const attention =
    phone.needsAttention || workspace.tone === 'warning' || step !== null

  return (
    <tr data-attention={attention || undefined}>
      <th scope="row">
        <span className={styles.clinicName}>{clinic.name}</span>
        {step ? (
          <span className={styles.nextStep}>
            <AlertTriangle size={13} aria-hidden="true" />
            Next: {step}
          </span>
        ) : null}
      </th>
      <td data-label="Workspace">
        <Status view={workspace} />
      </td>
      <td data-label="Phone & texting">
        <div className={styles.stack}>
          <Status view={phone} />
          {phone.numberPending ? (
            <Skeleton width="6.5rem" height="0.875rem" />
          ) : (
            <span className={styles.number}>
              {phoneNumberLabel(phone.number)}
            </span>
          )}
        </div>
      </td>
      <td data-label="Managers">
        {managerAccess.verified ? (
          <div className={styles.stack}>
            {managers.length === 0 ? (
              <span className={styles.muted}>No manager</span>
            ) : (
              <span className={styles.people}>
                <span className={styles.person}>
                  {managerLabel(managers[0]!)}
                </span>
                {managers.length > 1 ? (
                  <span
                    className={styles.more}
                    title={managers.slice(1).map(managerLabel).join(', ')}
                  >
                    +{managers.length - 1}
                  </span>
                ) : null}
              </span>
            )}
            {invitations.length > 0 ? (
              <span className={styles.muted}>
                {plural(
                  invitations.length,
                  'pending invite',
                  'pending invites'
                )}
              </span>
            ) : null}
          </div>
        ) : managerAccess.pending ? (
          <Skeleton width="8.5rem" height="0.875rem" />
        ) : (
          <span className={styles.muted}>Unavailable</span>
        )}
      </td>
      <td className={styles.actionCell}>
        <Button
          type="button"
          variant="secondary"
          aria-label={`Manage ${clinic.name}`}
          onClick={onManage}
        >
          Manage <ChevronRight size={15} aria-hidden="true" />
        </Button>
      </td>
    </tr>
  )
}
